import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewChild, inject, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { ScheduleCorrectionRequestsService } from 'src/app/services/api/schedule-correction-requests/schedule-correction-requests.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import {
  DisbursementCancellationCertification,
  DisbursementCancellationCoverMemo,
  DisbursementCancellationRequirement,
  DisbursementCancellationRequirementListParams,
  ScheduleCorrectionRequestItem,
} from 'src/app/services/models/schedule/schedule-correction-requests.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { SharedModule } from 'src/shared/shared.module';
import { getApiErrorMessage } from 'src/utils/http-error-message';
import { RoleID } from 'src/utils/enum/role.enum';

@Component({
  selector: 'app-disbursement-cancellation-queue',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SharedModule,
    ConfirmDialogComponent,
  ],
  templateUrl: './disbursement-cancellation-queue.component.html',
  styleUrl: './disbursement-cancellation-queue.component.scss',
})
export class DisbursementCancellationQueueComponent implements OnInit {
  @ViewChild('confirmDialog') confirmDialog?: ConfirmDialogComponent;

  readonly requirements = signal<DisbursementCancellationRequirement[]>([]);
  readonly tableLoading = signal(true);
  readonly actionLoading = signal(false);
  readonly detailOpen = signal(false);
  readonly selectedRequirement = signal<DisbursementCancellationRequirement | null>(null);
  readonly errorMessage = signal('');
  readonly page = signal(1);
  readonly limit = signal(50);
  readonly total = signal(0);
  readonly totalPages = signal(1);
  readonly summaryCount = signal(0);
  readonly pageSizeOptions = [10, 20, 50];

  private academicTermIdValue: number | null = null;
  private departmentIdValue: number | null = null;
  private keyword: string | null = null;
  private initialized = false;

  @Input()
  set academicTermId(value: number | null | undefined) {
    const next = value ?? null;
    if (next === this.academicTermIdValue) {
      return;
    }

    this.academicTermIdValue = next;
    if (this.initialized) {
      this.loadRequirements(1);
    }
  }

  @Input()
  set keywords(value: string | null | undefined) {
    const next = value?.trim() || null;
    if (next === this.keyword) {
      return;
    }

    this.keyword = next;
    if (this.initialized) {
      this.loadRequirements(1);
    }
  }

  @Input()
  set departmentId(value: number | null | undefined) {
    const next = value ?? null;
    if (next === this.departmentIdValue) {
      return;
    }

    this.departmentIdValue = next;
    if (this.initialized) {
      this.loadRequirements(1);
    }
  }

  private readonly authService = inject(AuthService);
  private readonly correctionRequestsService = inject(ScheduleCorrectionRequestsService);
  private readonly toast = inject(ToastService);

  ngOnInit(): void {
    this.initialized = true;
    this.loadRequirements();
  }

  get isFinanceOfficer(): boolean {
    return this.authService.getActiveRoleId() === RoleID.FINANCE_OFFICER;
  }

  get selectedRequester(): string {
    return this.requesterName(this.selectedRequirement());
  }

  get selectedReason(): string {
    const requirement = this.selectedRequirement();
    return this.displayValue(
      requirement?.reason
      ?? requirement?.description
      ?? requirement?.correction_request?.reason
      ?? requirement?.correction_request?.description,
    );
  }

  get selectedItems(): ScheduleCorrectionRequestItem[] {
    const requirement = this.selectedRequirement();
    return requirement?.items ?? requirement?.correction_request?.items ?? [];
  }

  get selectedSubjects(): { code: string; name: string; section: string }[] {
    const requirement = this.selectedRequirement();
    if (requirement?.affected_subjects != null && requirement.affected_subjects.length > 0) {
      return requirement.affected_subjects.map((subject) => ({
        code: this.displayValue(subject.code),
        name: this.displayValue(subject.name),
        section: this.displayValue(subject.section),
      }));
    }

    return this.selectedItems.map((item) => ({
      code: this.itemSubjectCode(item),
      name: this.itemSubjectName(item),
      section: this.displayValue(item.section ?? item.approvable?.section),
    }));
  }

  get selectedCertifications(): DisbursementCancellationCertification[] {
    return this.selectedRequirement()?.affected_certifications ?? [];
  }

  get selectedCoverMemos(): DisbursementCancellationCoverMemo[] {
    return this.selectedRequirement()?.cover_memos ?? [];
  }

  get selectedSummary() {
    const requirement = this.selectedRequirement();
    const summary = requirement?.summary ?? requirement?.cancellation_summary;
    if (summary != null) {
      return summary;
    }

    const memos = this.selectedCoverMemos;
    const cancelled = memos.filter((memo) => this.memoIsCancelled(memo)).length;
    return {
      total: memos.length,
      pending: Math.max(memos.length - cancelled, 0),
      cancelled,
    };
  }

  get confirmationMessage(): string {
    const count = this.selectedCoverMemos.length;
    const suffix = count === 1 ? '1 กลุ่ม' : `${count} กลุ่ม`;
    return `ยืนยันการยกเลิกกลุ่มใบปะหน้า ${suffix} ในระบบใช่หรือไม่ การดำเนินการนี้ไม่ใช่การคืนเงินหรือย้อนการจ่าย`;
  }

  onRefresh(): void {
    if (this.actionLoading()) {
      return;
    }

    this.loadRequirements(this.page());
  }

  onView(requirement: DisbursementCancellationRequirement): void {
    this.errorMessage.set('');
    this.selectedRequirement.set(requirement);
    this.detailOpen.set(true);
  }

  trackRequirement(_index: number, requirement: DisbursementCancellationRequirement): number {
    return requirement.id;
  }

  onCloseDetail(): void {
    if (this.actionLoading()) {
      return;
    }

    this.detailOpen.set(false);
    this.selectedRequirement.set(null);
    this.errorMessage.set('');
  }

  canConfirm(requirement: DisbursementCancellationRequirement | null = this.selectedRequirement()): boolean {
    return this.isFinanceOfficer
      && requirement?.status === 'waiting_finance_cancellation'
      && requirement.can_confirm
      && (requirement.cover_memos?.length ?? 0) > 0;
  }

  onOpenConfirm(): void {
    if (!this.canConfirm() || this.actionLoading()) {
      return;
    }

    this.errorMessage.set('');
    this.confirmDialog?.open();
  }

  onConfirmCancellation(): void {
    const requirement = this.selectedRequirement();
    if (this.actionLoading() || requirement == null || !this.canConfirm(requirement)) {
      return;
    }

    this.actionLoading.set(true);
    this.correctionRequestsService.financeCancelDisbursement(requirement.id).pipe(
      finalize(() => this.actionLoading.set(false)),
    ).subscribe({
      next: () => {
        this.confirmDialog?.close();
        this.detailOpen.set(false);
        this.selectedRequirement.set(null);
        this.errorMessage.set('');
        this.toast.toastSuccess('ยกเลิกกลุ่มใบปะหน้าเรียบร้อยแล้ว ระบบกำลังเปิดขั้นตอนแก้ไขต่อ');
        this.loadRequirements(this.page());
      },
      error: (error) => {
        this.errorMessage.set(getApiErrorMessage(error, 'ไม่สามารถยกเลิกกลุ่มใบปะหน้าได้ กรุณาลองใหม่อีกครั้ง'));
        this.toast.toastError(this.errorMessage());
      },
    });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.page() || this.actionLoading()) {
      return;
    }

    this.loadRequirements(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit <= 0 || limit === this.limit() || this.actionLoading()) {
      return;
    }

    this.limit.set(limit);
    this.loadRequirements(1);
  }

  requesterName(requirement: DisbursementCancellationRequirement | null): string {
    const requester = requirement?.requester
      ?? requirement?.requested_by
      ?? requirement?.correction_request?.requested_by;
    const parts = [requester?.academic_rank, requester?.full_name]
      .map((value) => String(value ?? '').trim())
      .filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : '-';
  }

  requestedAt(requirement: DisbursementCancellationRequirement | null): string {
    return this.formatDateTime(requirement?.requested_at ?? requirement?.correction_request?.requested_at);
  }

  itemSubjectCode(item: ScheduleCorrectionRequestItem): string {
    return this.displayValue(item.approvable?.subject_code ?? item.metadata?.['subject_code']);
  }

  itemSubjectName(item: ScheduleCorrectionRequestItem): string {
    return this.displayValue(item.approvable?.subject_name ?? item.metadata?.['subject_name']);
  }

  certificationLabel(certification: DisbursementCancellationCertification): string {
    if (this.displayValue(certification.label) !== '-') {
      return this.displayValue(certification.label);
    }

    const subject = [certification.subject_code, certification.subject_name]
      .map((value) => String(value ?? '').trim())
      .filter(Boolean)
      .join(' ');
    return subject || `รายการรับรอง #${certification.id}`;
  }

  certificationMemoNumber(certification: DisbursementCancellationCertification): string {
    return this.displayValue(certification.cover_memo_number);
  }

  memoNumber(memo: DisbursementCancellationCoverMemo): string {
    return this.displayValue(memo.document_number) === '-'
      ? `ใบปะหน้า #${memo.id}`
      : this.displayValue(memo.document_number);
  }

  memoStatusLabel(memo: DisbursementCancellationCoverMemo): string {
    if (this.memoIsCancelled(memo)) {
      return 'ยกเลิกแล้ว';
    }

    return this.displayValue(memo.status_label ?? memo.status_code ?? memo.cancellation_status);
  }

  memoIsCancelled(memo: DisbursementCancellationCoverMemo): boolean {
    return ['cancelled', 'canceled', 'ยกเลิกแล้ว'].includes(String(memo.cancellation_status ?? memo.status_code ?? '').trim().toLowerCase());
  }

  statusLabel(requirement: DisbursementCancellationRequirement): string {
    if (requirement.status === 'waiting_finance_cancellation') {
      return 'รอการเงินยกเลิกกลุ่มใบปะหน้า';
    }

    return this.displayValue(requirement.status_label ?? requirement.status);
  }

  displayValue(value: unknown): string {
    if (value == null) {
      return '-';
    }

    if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
      return '-';
    }

    const text = String(value).trim();
    return text === '' ? '-' : text;
  }

  formatDateTime(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('th-TH', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(date);
  }

  private loadRequirements(page = this.page()): void {
    this.page.set(page);
    this.errorMessage.set('');

    if (!this.isFinanceOfficer) {
      this.requirements.set([]);
      this.tableLoading.set(false);
      this.total.set(0);
      this.totalPages.set(1);
      this.summaryCount.set(0);
      return;
    }

    const params: DisbursementCancellationRequirementListParams = {
      status: 'waiting_finance_cancellation',
      page,
      limit: this.limit(),
    };
    if (this.academicTermIdValue != null) {
      params.academic_term_id = this.academicTermIdValue;
    }
    if (this.departmentIdValue != null) {
      params.department_id = this.departmentIdValue;
    }
    if (this.keyword != null) {
      params.keywords = this.keyword;
    }

    this.tableLoading.set(true);
    this.correctionRequestsService.getCancellationRequirements(params).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        this.requirements.set(response.requirements);
        this.page.set(response.page);
        this.limit.set(response.limit);
        this.total.set(response.total);
        this.totalPages.set(Math.max(response.total_pages, 1));
        this.summaryCount.set(response.summary?.pending ?? response.total);
      },
      error: (error) => {
        this.requirements.set([]);
        this.total.set(0);
        this.totalPages.set(1);
        this.summaryCount.set(0);
        this.errorMessage.set(getApiErrorMessage(error, 'ไม่สามารถโหลดคิวการยกเลิกใบปะหน้าได้'));
        this.toast.toastError(this.errorMessage());
      },
    });
  }
}
